import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  alpha,
  useTheme,
  Tooltip
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface Contact {
  UserID: number;
  FirstName: string;
  LastName: string;
  EmailID: string;
  PhoneNumber: string;
  InsertDate: string;
  Active: boolean;
  IsApproved: boolean | null;
  ReasonOfRejection: string | null;
}

interface Business {
  Business: string;
  FederalID: string;
  Address1: string;
  Address2: string;
  City: string;
  State: string;
  ZipCode: string;
  Contacts: Contact[];
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    TotalPages: number;
    CurrentPage: number;
    Size: string;
    Data: Business[];
  };
}

type SortOrder = 'asc' | 'desc';

interface HeadCell {
  id: keyof Contact | 'actions';
  label: string;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'FirstName', label: 'First Name', sortable: true },
  { id: 'LastName', label: 'Last Name', sortable: true },
  { id: 'EmailID', label: 'Email', sortable: true },
  { id: 'PhoneNumber', label: 'Phone', sortable: true },
  { id: 'InsertDate', label: 'Registration Date', sortable: true },
  { id: 'IsApproved', label: 'Status', sortable: false }
];

const MyContacts: React.FC = () => {
  const theme = useTheme();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [showMessage, setShowMessage] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [sortBy, setSortBy] = useState<keyof Contact>('FirstName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const apiPage = page + 1;
      const response = await httpRequest(
        'GET',
        `/auth/myContacts?Page=${apiPage}&Size=${rowsPerPage}&FederalID=${userDetails?.VendorDetails?.FederalID}`,
        {},
        {}
      );

      if (response.status === 'success') {
        const apiData = response.data as ApiResponse['data'];
        setBusinesses(apiData.Data);
        setTotalRecords(apiData.TotalRecords);
      } else {
        setMessage('Failed to load contacts. Please try again.');
        setShowMessage(true);
      }
    } catch (error) {
      setMessage('Error loading contacts. Please try again.');
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [page, rowsPerPage]);

  const handleRequestSort = (property: keyof Contact) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  const getStatusDisplay = (contact: Contact) => {
    if (contact.IsApproved === true) {
      return (
        <Chip
          label="Approved"
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main
          }}
        />
      );
    } else if (contact.IsApproved === false) {
      return (
        <Tooltip title={contact.ReasonOfRejection || ''} arrow>
          <Chip
            label="Rejected"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              cursor: 'pointer'
            }}
          />
        </Tooltip>
      );
    } else {
      return (
        <Chip
          label="Pending"
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.main
          }}
        />
      );
    }
  };

  const sortedContacts = (contacts: Contact[]) => {
    return [...contacts]
      .sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];
        if (typeof valueA === 'string' && typeof valueB === 'string')
          return valueA.localeCompare(valueB);
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      })
      .map((c) => c);
  };

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 100,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          My Contacts
        </Typography>
      </Box>

      {showMessage && (
        <Alert
          severity="error"
          onClose={() => setShowMessage(false)}
          sx={{ mb: 3 }}
        >
          {message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        businesses.map((business) => (
          <Paper key={business.FederalID} sx={{ p: 2, mb: 4, borderRadius: 2 }}>
            {/* Business Info */}
            <Box key={business.FederalID} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', color: theme.palette.primary.dark }}
              >
                {business.Business}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Federal ID:</strong> {business.FederalID}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Address:</strong> {business.Address1}
                {business.Address2 ? `, ${business.Address2}` : ''},{' '}
                {business.City}, {business.State} Zip: {business.ZipCode}
              </Typography>
            </Box>

            {/* Contacts Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={
                          sortBy === headCell.id ? sortOrder : false
                        }
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={sortBy === headCell.id}
                            direction={
                              sortBy === headCell.id ? sortOrder : 'asc'
                            }
                            onClick={() =>
                              headCell.id !== 'actions' &&
                              handleRequestSort(headCell.id as keyof Contact)
                            }
                          >
                            {headCell.label}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {business.Contacts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={headCells.length}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography variant="body1" color="text.secondary">
                            No contacts found.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedContacts(business.Contacts).map((contact) => (
                      <TableRow
                        key={contact.UserID}
                        hover
                        sx={{
                          '&:nth-of-type(even)': {
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.4
                            )
                          }
                        }}
                      >
                        <TableCell>{contact.FirstName}</TableCell>
                        <TableCell>{contact.LastName}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {contact.EmailID}
                        </TableCell>
                        <TableCell>{contact.PhoneNumber}</TableCell>
                        <TableCell>{formatDate(contact.InsertDate)}</TableCell>
                        <TableCell>{getStatusDisplay(contact)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      <TablePagination
        component="div"
        count={totalRecords}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default MyContacts;
